import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import { FrostedBackdropTarget } from "@-/dev.improve.haniverse.components.ui.frosted-surface";
import { LearnLayoutPrompts } from "#learn-layout-prompts";
import { NavToolbarProvider } from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.toolbar";
import { StudySessionBoundary } from "#study-session-boundary";
import { PrimaryControlsStripBack } from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.back";
import { Stack, type RoutePath } from "@-/com.npmjs.expo-router";
import path from "@-/com.npmjs.pathe";
import { View } from "@-/com.npmjs.react-native";
import { useLearnStackReset } from "#use-learn-stack-reset";
import { LearnCardsLayer, LearnCardsRequested } from "#learn-cards-layer";
import { LearnMapTabPress } from "#learn-map-tab-press";
import { PrimaryControlsStripExpansion } from "@-/dev.improve.haniverse.routes.nav._components.primary-controls-strip.expansion";
import { LearnPrimaryControlsStripExpansionContent } from "@-/dev.improve.haniverse.routes.nav.app.learn._components.primary-controls-strip-expansion-content";
import { LearnLayoutContextProvider } from "@-/dev.improve.haniverse.routes.nav.app.learn._context";
import {
  LearnMapModeProvider,
  useLearnMapModeSelector,
} from "@-/dev.improve.haniverse.routes.nav.app.learn._lib.map-mode";
import { LearnDeckHost } from "@-/dev.improve.haniverse.routes.nav.app.learn.deck";
import { MapLayer } from "@-/dev.improve.haniverse.routes.nav.app.learn.map";
import {
  LearnSummaryHost,
  LearnSummaryMapWelcome,
  useLearnSummaryMapHomeExit,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.summary";
import { NavigationQueryActivityContext } from "@-/com.npmjs.tanstack__react-query";
import { useToolbarGridMetrics } from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import { LearnLayoutToolbar } from "@-/dev.improve.haniverse.routes.nav.app.learn._components.toolbar";
import {
  useVoiceStudyControlsGate,
  VoiceStudyControls,
  VoiceStudyModeExitSync,
} from "@-/dev.improve.haniverse.routes.nav.app.learn._components.voice-study";
import type { ReactNode } from "react";

const TRANSPARENT_NATIVE_STACK = { nativeContainerStyle: { backgroundColor: "transparent" } };

/** Navigation logic for the map's three card-entry controls; renders no flashcard UI. */
function LearnMapHomeExit() {
  useLearnSummaryMapHomeExit();
  return null;
}

/**
 * The Voice pill and its exit sync. A child of the Learn provider (which the layout itself renders),
 * so it can read the session; re-renders only when a session starts, fails, or ends.
 */
function LearnLayoutVoiceStudy() {
  const voiceStudyControlsGate = useVoiceStudyControlsGate();
  return (
    <>
      {voiceStudyControlsGate !== null && <VoiceStudyControls gate={voiceStudyControlsGate} />}
      <VoiceStudyModeExitSync />
    </>
  );
}

/**
 * Learn's Stack is a set of transparent history entries over Learn's persistent surfaces. While
 * the map itself is the screen, its `index` entry lies over the map beneath the whole scene, so the
 * Stack lets every touch through to it. A page opened over the map takes touches as usual.
 */
function LearnStackTouches({ children }: { children: ReactNode }) {
  const isMapShown = useLearnMapModeSelector((mode) => mode.isMapShown) ?? false;
  return (
    <View className="flex-1" pointerEvents={isMapShown ? "none" : "box-none"}>
      {children}
    </View>
  );
}

/**
 * Learn's content: the cards, the Stack over them and the prompts. While the map is on screen or
 * on its way (and no page is open over it), it draws above Learn's toolbar, so the card leaving
 * for the map passes over the toolbar row rather than under it. Only the card ever reaches the row
 * in portrait; landscape's toolbar is a column beside the card, kept above the cards' backdrop.
 */
function LearnContentLayer({ children }: { children: ReactNode }) {
  const { isLandscape } = useToolbarGridMetrics();
  const isAboveToolbar =
    useLearnMapModeSelector((mode) => mode.isMapPresent && (mode.isMapShown || !mode.isMapOpen)) ??
    false;
  return (
    // Keep the deck's z-10 surface and z-20 recharge inside this content layer. Fabric otherwise
    // flattens them beside the earlier z-10 toolbar, so the later deck surface covers Undo, the map
    // button and Redo when the toolbar overlays it in landscape.
    <FrostedBackdropTarget
      className={isAboveToolbar && !isLandscape ? "z-20 flex-1" : "flex-1"}
      collapsable={false}
      pointerEvents="box-none"
    >
      {children}
    </FrostedBackdropTarget>
  );
}

/**
 * Learn has two modes: the cards (the persistent deck and the summary over it) and the map
 * (`/learn/map`, `LearnMapModeProvider`). The map lies beneath the whole scene. Opening it slides
 * the card up and out of view over Learn's toolbar, which is replaced by the map's beneath it; the
 * cards' backdrop dissolves into the map, the map's sheet slides in from the bottom over the
 * controls below the card, and the More menu's slot turns into Back. Back reverses all of it.
 *
 * The map is also Learn's home: a cold launch opens Learn on it, its sheet greeting the learner
 * (`LearnSummaryMapWelcome`) with the way into the cards, and its top-left control is Learn.
 * Card hosts and chrome mount only once a learner requests them through the map's controls or
 * an explicit cards route; map startup renders neither host beneath it.
 */
export function LearnLayout({ base, routes }: { base: string; routes: readonly RoutePath[] }) {
  const [isEdgeNavigationEnabled] = useDeviceSetting("isEdgeNavigationEnabled");
  // Subscribes to "reset onboarding" and re-shows the Learn Stack's `index` screen — without
  // switching the focused bottom tab — so the just-restored intro slides surface there.
  useLearnStackReset();

  return (
    <LearnMapModeProvider>
      <LearnLayoutContextProvider>
        <PrimaryControlsStripBack hidden />
        <LearnCardsRequested>
          <PrimaryControlsStripExpansion>
            <LearnPrimaryControlsStripExpansionContent />
          </PrimaryControlsStripExpansion>
        </LearnCardsRequested>
        <NavToolbarProvider>
          <StudySessionBoundary>
            {/* Beneath everything, across the whole scene (so it runs under the status bar and
                the map's own floating toolbar): the map's silhouette, and the map once opened. */}
            <MapLayer welcome={<LearnSummaryMapWelcome />} />
            <LearnMapHomeExit />
            <LearnMapTabPress />
            <LearnLayoutToolbar />
            {/* A plain View: Uniwind only maps `className` on core components. It stays clear so
                the map shows through once the cards have left; the cards paint their own surface. */}
            <LearnContentLayer>
              <LearnCardsLayer>
                <NavigationQueryActivityContext value={true}>
                  <LearnDeckHost />
                </NavigationQueryActivityContext>
                {/* The summary is persistent too, above the deck: Start hands the screen to the
                    deck at once, and the welcome card flies off over it. Its route is a history
                    entry. */}
                <LearnSummaryHost />
              </LearnCardsLayer>
              <LearnStackTouches>
                <Stack
                  screenOptions={{
                    animation: "none",
                    animationMatchesGesture: false,
                    contentStyle: { backgroundColor: "transparent" },
                    fullScreenGestureEnabled: false,
                    gestureEnabled: isEdgeNavigationEnabled,
                    headerShown: false,
                    keyboardHandlingEnabled: false,
                  }}
                  // The persistent cards render beneath this Stack and must show through its
                  // routes (the summary card's underlay is the deck's first card), as must the
                  // map. A transparent `contentStyle` only clears each screen; the native stack
                  // also paints the theme background on its navigation-controller view, which
                  // covered the deck entirely.
                  unstable_nativeProps={TRANSPARENT_NATIVE_STACK}
                >
                  {routes.map((route) => {
                    const routeName = path.relative(base, route) || "index";
                    return <Stack.Screen key={route} name={routeName} />;
                  })}
                </Stack>
              </LearnStackTouches>
              <LearnCardsRequested>
                <LearnLayoutPrompts />
                <LearnLayoutVoiceStudy />
              </LearnCardsRequested>
            </LearnContentLayer>
          </StudySessionBoundary>
        </NavToolbarProvider>
      </LearnLayoutContextProvider>
    </LearnMapModeProvider>
  );
}
